import React, { useState, useEffect } from 'react';
import { User, Placement } from '../types';
import { X, Save, ArrowLeft, Building2, User as UserIcon, Briefcase, Calendar, DollarSign, FileText, ExternalLink, CheckCircle, Clock, AlertCircle, Tag, Edit3, Loader2, Upload, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { SearchableSelect } from './SearchableSelect';
import { supabase, supabase_search_talent_pool, openStorageDocument } from '../services/supabaseService';
import { searchDbIlikePattern, searchTextIncludesAny } from '../utils/searchText';
import { PlacementNotes } from './PlacementNotes';
import ClientDetailModal from './ClientDetailModal';
import CandidateDetailModal from './CandidateDetailModal';
import VacancyDetailModal from './VacancyDetailModal';

interface PlacementDetailProps {
    placement: Placement | null;
    user: User;
    onClose: () => void;
    onSave: (placement: any) => void;
    onDelete?: (id: string) => void;
    onNext?: () => void;
    onPrev?: () => void;
    hasNext?: boolean;
    hasPrev?: boolean;
    clientSettings?: any;
}

const STATUS_OPTIONS = [
    { value: 'Voorgedragen',        label: 'Voorgedragen',        color: 'bg-violet-500' },
    { value: 'Goedgekeurd',         label: 'Goedgekeurd',         color: 'bg-emerald-500' },
    { value: 'Afgewezen',           label: 'Afgewezen',           color: 'bg-red-500' },
    { value: 'Opnieuw voorgedragen',label: 'Opnieuw voorgedragen',color: 'bg-orange-500' },
    { value: 'Voltooid',            label: 'Voltooid',            color: 'bg-blue-500' },
    { value: 'Beëindigd',           label: 'Beëindigd',           color: 'bg-slate-500' },
];

const INPUT = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-medium bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all placeholder:text-slate-300';
const LABEL = 'block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1';
let cachedVacancies: any[] | null = null;
let cachedCandidates: any[] | null = null;

const PlacementDetail: React.FC<PlacementDetailProps> = ({ placement, user, onClose, onSave, onDelete, onNext, onPrev, hasNext, hasPrev, clientSettings }) => {
    const [isEditing, setIsEditing] = useState(!placement?.id);
    const inputClass = isEditing ? INPUT : "w-full py-1 text-[13px] font-bold text-slate-800 bg-transparent border-transparent outline-none pointer-events-none truncate appearance-none shadow-none px-0";
    const [activeTab, setActiveTab] = useState('overzicht');
    const [formData, setFormData] = useState<any>({
        status: 'Voorgedragen',
        contract_signed: false,
        data: {},
        ...placement,
        start_date: placement?.start_date ? placement.start_date.slice(0, 10) : '',
        end_date: placement?.end_date ? placement.end_date.slice(0, 10) : '',
    });
    const [vacancies, setVacancies] = useState<any[]>(cachedVacancies || []);
    const [candidates, setCandidates] = useState<any[]>(cachedCandidates || []);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedClientModal, setSelectedClientModal] = useState<any>(null);
    const [selectedCandidateModalId, setSelectedCandidateModalId] = useState<string | null>(null);
    const [selectedVacancyModalId, setSelectedVacancyModalId] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteInput, setDeleteInput] = useState('');
    const [docUploading, setDocUploading] = useState(false);
    const [docPendingDelete, setDocPendingDelete] = useState<any | null>(null);

    /** Talent pool search returns `{ candidate: { name, ... } }` — flatten for SearchableSelect. */
    const normalizeCandidateOption = (row: any) => {
        const c = row?.candidate || row;
        const name = (c?.name || row?.name || '').trim() || 'Onbekende kandidaat';
        const num = c?.candidate_number || c?.number || row?.candidate_number;
        return {
            id: c?.id || row?.candidate_id || row?.id,
            name,
            email: c?.email || row?.email,
            role: c?.role || row?.role,
            candidate_number: num,
            bullhorn_id: c?.bullhorn_id || c?.data?.id,
            candidate: c,
        };
    };

    const searchCandidates = async (q: string) => {
        const rows = await supabase_search_talent_pool(user.clientId, q);
        return (rows || []).map(normalizeCandidateOption);
    };

    const formatCandidateLabel = (opt: any) => {
        const name = opt?.name || opt?.candidate?.name || 'Onbekende kandidaat';
        const num = opt?.candidate_number || opt?.candidate?.candidate_number || opt?.bullhorn_id;
        const parts = [name];
        if (num) parts.push(`(${num})`);
        return parts.join(' ');
    };

    const renderCandidateOption = (opt: any) => {
        const name = opt?.name || opt?.candidate?.name || 'Onbekende kandidaat';
        const num = opt?.candidate_number || opt?.candidate?.candidate_number;
        const sub = [opt?.email || opt?.candidate?.email, opt?.role || opt?.candidate?.role].filter(Boolean).join(' · ');
        return (
            <div className="flex flex-col gap-0.5 min-w-0">
                <div className="font-semibold text-slate-800 truncate">
                    {num ? <span className="font-mono text-indigo-600 mr-1.5">#{num}</span> : null}
                    {name}
                </div>
                {sub ? <div className="text-[10px] text-slate-500 truncate">{sub}</div> : null}
            </div>
        );
    };
    
    const VACANCY_SEARCH_SELECT =
        'id, title, vacancy_number, bullhorn_id, status, data, opdrachtgever_id, client_id, eindklant_id, opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(name), client:clients!vacancies_client_id_fkey(name), eindklant:clients!vacancies_eindklant_id_fkey(name, logo_url)';

    const filterVacancyRows = (rows: any[], term: string) => {
        if (!term) return rows;
        return rows.filter((v: any) =>
            searchTextIncludesAny(
                term,
                v.title,
                v.vacancy_number != null ? String(v.vacancy_number) : '',
                v.bullhorn_id,
                v.opdrachtgever?.name,
                v.client?.name,
                v.eindklant?.name,
                v.data?.location,
                v.data?.city,
                v.data?.werkplek
            )
        );
    };

    const searchVacancies = async (q: string) => {
        const term = q.trim();
        const dbPattern = searchDbIlikePattern(term);
        const base = () => {
            let query = supabase
                .from('vacancies')
                .select(VACANCY_SEARCH_SELECT)
                .order('created_at', { ascending: false })
                .limit(term ? 120 : 50);
            if (user?.clientId) {
                query = query.eq('client_id', user.clientId);
            }
            return query;
        };

        const runSearch = async () => {
            let query = base();
            // vacancy_number is integer — ilike only on title (text)
            if (term) {
                query = query.ilike('title', dbPattern);
            }
            return query;
        };

        let { data, error } = await runSearch();

        if (error) {
            console.warn('[PlacementDetail] Vacancy search (with joins):', error.message);
            let fallback = supabase
                .from('vacancies')
                .select('id, title, vacancy_number, bullhorn_id, status, data, opdrachtgever_id, client_id, eindklant_id')
                .order('created_at', { ascending: false })
                .limit(term ? 120 : 50);
            if (user?.clientId) fallback = fallback.eq('client_id', user.clientId);
            if (term) fallback = fallback.ilike('title', dbPattern);
            const fb = await fallback;
            if (fb.error) {
                console.error('[PlacementDetail] Vacancy search failed:', fb.error.message, fb.error.details);
                return [];
            }
            data = fb.data;
        }

        let rows = filterVacancyRows(data || [], term);

        // Zoeken op numeriek vacaturenummer (geen ilike op integer)
        const num = term && /^\d+$/.test(term) ? parseInt(term, 10) : NaN;
        if (!Number.isNaN(num) && user?.clientId) {
            const { data: byNum } = await supabase
                .from('vacancies')
                .select(VACANCY_SEARCH_SELECT)
                .eq('client_id', user.clientId)
                .eq('vacancy_number', num)
                .maybeSingle();
            if (byNum && !rows.some((r: any) => r.id === byNum.id)) {
                rows = [byNum, ...rows];
            }
        }

        return rows.slice(0, 25);
    };

    const formatVacancyLabel = (opt: any) => {
        const num = opt?.vacancy_number || opt?.bullhorn_id;
        const org = opt?.opdrachtgever?.name || opt?.client?.name;
        const parts = [opt?.title || 'Vacature'];
        if (num) parts.push(`(${num})`);
        if (org) parts.push(`· ${org}`);
        return parts.join(' ');
    };

    const renderVacancyOption = (opt: any) => {
        const num = opt?.vacancy_number || opt?.bullhorn_id;
        const org = opt?.opdrachtgever?.name || opt?.client?.name;
        const werkplek = opt?.eindklant?.name || opt?.data?.location || opt?.data?.werkplek;
        return (
            <div className="flex flex-col gap-0.5 min-w-0">
                <div className="font-semibold text-slate-800 truncate">
                    {num ? <span className="font-mono text-indigo-600 mr-1.5">#{num}</span> : null}
                    {opt?.title || '—'}
                </div>
                {(org || werkplek) && (
                    <div className="text-[10px] text-slate-500 truncate">
                        {[org, werkplek].filter(Boolean).join(' · ')}
                    </div>
                )}
            </div>
        );
    };

    const searchClients = async (q: string) => {
        const safeQ = q.replace(/[%_"']/g, '');
        const { data, error } = await supabase.from('clients').select('id, name, logo_url')
            .ilike('name', `%${safeQ}%`)
            .limit(20);
        if (error) console.error("Client search error:", error);
        return data || [];
    };

    /** CRM-contacten horen bij `linked_client_id` (= bedrijf); `client_id` is de Werftools-tenant. */
    const searchContacts = async (q: string) => {
        const safeQ = q.replace(/"/g, '');
        const vac = formData.vacancy;
        const orgId =
            formData.data?.eindklant_id ||
            formData.eindklant_id ||
            vac?.eindklant_id ||
            formData.data?.opdrachtgever_id ||
            formData.opdrachtgever_id ||
            vac?.opdrachtgever_id ||
            vac?.client_id;
        if (!orgId) {
            return [];
        }
        let query = supabase
            .from('client_contacts')
            .select('id, first_name, last_name, email')
            .eq('client_id', user.clientId)
            .eq('linked_client_id', orgId);
        if (safeQ.trim()) {
            query = query.or(`first_name.ilike."%${safeQ}%",last_name.ilike."%${safeQ}%"`);
        }
        const { data, error } = await query.limit(40);
        if (error) console.error('Contact search error:', error);
        return data || [];
    };

    const searchRecruiters = async (q: string) => {
        let query = supabase.from('profiles').select('id, name, email').eq('client_id', user.clientId);
        if (q) query = query.ilike('name', `%${q}%`);
        const { data } = await query.limit(100);
        return data || [];
    };

    useEffect(() => {
        if (!placement) {
            setFormData({
                status: 'Voorgedragen',
                contract_signed: false,
                data: {},
                start_date: '',
                end_date: '',
            });
            setIsEditing(true);
            return;
        }
        const vac = placement.vacancy;
        const d = { ...(placement.data || {}) };
        const ekId = placement.eindklant_id || vac?.eindklant_id || d.eindklant_id;
        const opId = placement.opdrachtgever_id || vac?.opdrachtgever_id || vac?.client_id || d.opdrachtgever_id;
        const ekName = placement.eindklant?.name || vac?.eindklant?.name || placement.end_client || d.eindklant;
        const opName = placement.opdrachtgever?.name || vac?.opdrachtgever?.name || vac?.client?.name || d.opdrachtgever;
        if (ekId) d.eindklant_id = ekId;
        if (opId) d.opdrachtgever_id = opId;
        if (ekName) d.eindklant = d.eindklant || ekName;
        if (opName) d.opdrachtgever = d.opdrachtgever || opName;

        setFormData({
            ...placement,
            contract_type: (placement as any).contract_type || d.contract_type || '',
            end_client: placement.end_client || ekName || '',
            start_date: placement.start_date ? placement.start_date.slice(0, 10) : '',
            end_date: placement.end_date ? placement.end_date.slice(0, 10) : '',
            data: d,
        });
        setIsEditing(!placement.id || String(placement.id).startsWith('app-'));
    }, [placement]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleDataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, data: { ...prev.data, [name]: value } }));
    };

    const stripPlacementPayload = (fd: any) => {
        const { candidate, vacancy, recruiter, eindklant, opdrachtgever, client, ...payload } = fd;
        return payload;
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(stripPlacementPayload(formData));
        } finally {
            setIsSaving(false);
        }
    };

    /** Documenten-tab: niet afhankelijk van «Bewerken» — alleen vaste plaatsing-ID nodig. */
    const canEditPlacementDocs = Boolean(placement?.id && !String(placement.id).startsWith('app-'));

    const bullhornCandidate = formData.data?.candidate;
    const bullhornVacancy = formData.data?.jobOrder;

    const selectedCandidate = formData.candidate || candidates.find(c => c.id === formData.candidate_id) || 
                              candidates.find(c => c.data?.id === bullhornCandidate?.id || c.number === `BH-${bullhornCandidate?.id}`);
    const selectedVacancy = formData.vacancy || vacancies.find(v => v.id === formData.vacancy_id) || 
                            vacancies.find(v => v.data?.id === bullhornVacancy?.id || v.number === `BH-${bullhornVacancy?.id}`);

    const candidateName = selectedCandidate?.name || (bullhornCandidate ? `${bullhornCandidate.firstName || ''} ${bullhornCandidate.lastName || ''}`.trim() : '');
    const vacancyTitle = selectedVacancy?.title || bullhornVacancy?.title || '';
    const opdrachtgeverName = selectedVacancy?.opdrachtgever?.name || formData.data?.clientCorporation?.name || bullhornVacancy?.clientCorporation?.name || '';
    
    // Numbers
    const displayNum = formData.number ? formData.number.replace(/^(BH-|PL-)/, '') : '';
    const candNum = selectedCandidate?.number?.replace(/^(BH-|CD-)/, '') || bullhornCandidate?.id || '';
    const vacNum = selectedVacancy?.vacancy_number || selectedVacancy?.number?.replace(/^(BH-|VAC-)/, '') || selectedVacancy?.bullhorn_id || bullhornVacancy?.id || '';
    
    const clientLogo = placement?.resolved_eindklant?.logo_url || placement?.resolved_opdrachtgever?.logo_url || formData.data?.eindklant_logo || formData.data?.opdrachtgever_logo || selectedVacancy?.eindklant?.logo_url || selectedVacancy?.opdrachtgever?.logo_url;
    const endClientName = placement?.resolved_eindklant?.name || formData.end_client || selectedVacancy?.eindklant?.name || formData.data?.eindklant || '';
    const opdrachtgeverNameStr = placement?.resolved_opdrachtgever?.name || formData.data?.opdrachtgever || opdrachtgeverName || '';

    const recruiterName = formData.recruiter?.name || selectedVacancy?.recruiter?.name || selectedVacancy?.owner?.name || (bullhornVacancy?.owner ? `${bullhornVacancy.owner.firstName || ''} ${bullhornVacancy.owner.lastName || ''}`.trim() : '') || (formData.data?.owner ? `${formData.data.owner.firstName || ''} ${formData.data.owner.lastName || ''}`.trim() : '');

    const tabs = [
        { id: 'overzicht', label: 'Overzicht' },
        { id: 'financieel', label: 'Financieel' },
        { id: 'notities', label: 'Notities' },
        { id: 'documenten', label: 'Documenten' },
    ];

    const handleDeleteConfirm = () => {
        if (deleteInput.toLowerCase() === 'verwijderen' && onDelete && placement?.id) {
            onDelete(placement.id);
        }
    };

    const handlePlacementDocumentUpload = async (file: File) => {
        const pid = placement?.id;
        if (!pid || String(pid).startsWith('app-')) {
            alert('Sla de plaatsing eerst op zodat er een vaste ID is, daarna kun je documenten uploaden.');
            return;
        }
        if (!user?.clientId) {
            alert('Geen organisatie-context; upload niet mogelijk.');
            return;
        }
        setDocUploading(true);
        try {
            const safeName = (file.name || 'document').replace(/[^a-zA-Z0-9.\-_]/g, '_').slice(0, 180);
            const storagePath = `${user.clientId}/placements/${pid}/${Date.now()}_${safeName}`;
            const { error: uploadError } = await supabase.storage.from('documents').upload(storagePath, file, {
                contentType: file.type || 'application/octet-stream',
                upsert: false,
            });
            if (uploadError) throw uploadError;
            const { data: pub } = supabase.storage.from('documents').getPublicUrl(storagePath);
            const newDoc = {
                id: crypto.randomUUID(),
                name: file.name,
                contentType: file.type || 'application/octet-stream',
                dateAdded: new Date().toISOString(),
                source: 'Werftools',
                storage_path: storagePath,
                public_url: pub?.publicUrl ?? null,
            };
            const currentDocs = formData.data?.uploaded_documents || [];
            const nextData = { ...formData.data, uploaded_documents: [...currentDocs, newDoc] };
            const mergedForm = { ...formData, data: nextData };
            setFormData(mergedForm);
            setIsSaving(true);
            try {
                await onSave(stripPlacementPayload(mergedForm));
                window.dispatchEvent(
                    new CustomEvent('wt:toast', {
                        detail: { message: 'Document toegevoegd en opgeslagen bij deze plaatsing.', type: 'success' },
                    })
                );
            } catch (saveErr: any) {
                await supabase.storage.from('documents').remove([storagePath]);
                setFormData((prev: any) => ({
                    ...prev,
                    data: { ...prev.data, uploaded_documents: currentDocs },
                }));
                alert('Opslaan mislukt: ' + (saveErr?.message || String(saveErr)));
            } finally {
                setIsSaving(false);
            }
        } catch (e: any) {
            alert('Upload mislukt: ' + (e?.message || String(e)));
        } finally {
            setDocUploading(false);
        }
    };

    const removePlacementUploadedDoc = async (file: any) => {
        const prevDocs = formData.data?.uploaded_documents || [];
        const pathToRemove = file?.storage_path as string | undefined;
        if (pathToRemove) {
            const { error } = await supabase.storage.from('documents').remove([pathToRemove]);
            if (error) console.warn('[PlacementDetail] storage remove:', error);
        }
        const updated = prevDocs.filter((d: any) => d.id !== file.id);
        const nextData = { ...formData.data, uploaded_documents: updated };
        const mergedForm = { ...formData, data: nextData };
        setFormData(mergedForm);
        setIsSaving(true);
        try {
            await onSave(stripPlacementPayload(mergedForm));
            window.dispatchEvent(
                new CustomEvent('wt:toast', { detail: { message: 'Document verwijderd uit het dossier.', type: 'success' } })
            );
        } catch (saveErr: any) {
            setFormData((prev: any) => ({ ...prev, data: { ...prev.data, uploaded_documents: prevDocs } }));
            alert('Verwijderen kon niet worden opgeslagen: ' + (saveErr?.message || String(saveErr)));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-[#f4f6f9] font-sans relative">
            {docPendingDelete && (
                <div className="absolute inset-0 z-[55] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-fadeIn">
                        <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <FileText size={24} />
                        </div>
                        <h3 className="text-lg font-black text-center text-slate-900 mb-2">Document verwijderen?</h3>
                        <p className="text-sm text-center text-slate-500 mb-2">
                            Het bestand wordt uit opslag gehaald en uit deze plaatsing gehaald.
                        </p>
                        <p className="text-xs font-bold text-slate-800 text-center truncate px-2 mb-6" title={docPendingDelete.name}>
                            {docPendingDelete.name || 'Document'}
                        </p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setDocPendingDelete(null)}
                                disabled={isSaving}
                                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                            >
                                Annuleren
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const d = docPendingDelete;
                                    setDocPendingDelete(null);
                                    if (d) await removePlacementUploadedDoc(d);
                                }}
                                disabled={isSaving}
                                className="flex-1 py-3 bg-[#e51b23] hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 size={16} className="animate-spin" /> : null}
                                Verwijderen
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showDeleteConfirm && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-fadeIn">
                        <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <Trash2 size={24} />
                        </div>
                        <h3 className="text-lg font-black text-center text-slate-900 mb-2">Plaatsing Verwijderen?</h3>
                        <p className="text-sm text-center text-slate-500 mb-6">
                            Dit is definitief en kan niet ongedaan worden gemaakt. Typ <strong>verwijderen</strong> om te bevestigen.
                        </p>
                        <input
                            type="text"
                            value={deleteInput}
                            onChange={(e) => setDeleteInput(e.target.value)}
                            placeholder="Typ verwijderen..."
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 mb-6"
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}
                                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-colors"
                            >
                                Annuleren
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleteInput.toLowerCase() !== 'verwijderen'}
                                className="flex-1 py-3 bg-[#e51b23] hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Verwijder
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex-1 bg-white flex flex-col overflow-hidden m-4 rounded-xl shadow-sm border border-slate-200">
                {/* ─── COMPACT HEADER ─── */}
                <div className="p-3 md:p-4 border-b border-gray-100 bg-white flex flex-col gap-2 shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider text-[#e51b23] bg-red-50 border border-red-100 hover:bg-red-100 transition-all shadow-sm active:scale-95"
                            >
                                <ArrowLeft size={14} /> Terug
                            </button>
                            {(hasPrev || hasNext) && (
                                <div className="flex items-center gap-1 ml-2 bg-slate-100/50 p-0.5 rounded-lg border border-slate-200">
                                    <button 
                                        onClick={onPrev} 
                                        disabled={!hasPrev}
                                        className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 transition-all"
                                        title="Vorige plaatsing"
                                    >
                                        <ChevronLeft size={14} />
                                    </button>
                                    <button 
                                        onClick={onNext} 
                                        disabled={!hasNext}
                                        className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:shadow-sm disabled:opacity-30 transition-all"
                                        title="Volgende plaatsing"
                                    >
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex items-center gap-2 shrink-0">
                            {isEditing ? (
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-[#F1FDF7] text-[#007B55] hover:bg-[#E2F8EE] border border-[#BDE8D3] rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95"
                                >
                                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
                                </button>
                            ) : (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-[#F8FAFC] text-[#334155] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95"
                                >
                                    <Edit3 size={14} /> Bewerken
                                </button>
                            )}

                            {placement?.id && onDelete && (
                                <button onClick={() => setShowDeleteConfirm(true)} className="p-1.5 bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg transition-colors" title="Verwijder Plaatsing">
                                    <Trash2 size={14} />
                                </button>
                            )}
                            <button onClick={onClose} className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-400 rounded-lg transition-colors" title="Annuleren">
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    <div className="flex items-start justify-between gap-3 md:gap-4 mt-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-2xl font-black text-slate-300 shadow-sm shrink-0 overflow-hidden relative group">
                                {clientLogo ? <img src={clientLogo} alt="Logo" className="w-full h-full object-cover" /> : <Briefcase size={24} />}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1 w-full flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-lg md:text-xl font-black text-slate-900 tracking-tight truncate">
                                            {vacancyTitle || 'Nieuwe Plaatsing'}
                                        </h2>
                                        {displayNum && (
                                            <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200 uppercase tracking-widest">
                                                #{displayNum}
                                            </span>
                                        )}
                                    </div>
                                    <select
                                        name="status"
                                        value={formData.status || 'Voorgedragen'}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        className="text-[10px] md:text-[11px] font-black uppercase tracking-widest pl-3 pr-8 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 cursor-pointer appearance-none shadow-sm transition-all"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'right .7rem top 50%',
                                            backgroundSize: '.6rem auto',
                                        }}
                                    >
                                        {STATUS_OPTIONS.map(stat => <option key={stat.value} value={stat.value}>{stat.label}</option>)}
                                    </select>

                                    <span className="text-[9px] md:text-[10px] font-black tracking-widest px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 uppercase flex items-center gap-1">
                                        🗓️ START: {formData.start_date ? new Date(formData.start_date).toLocaleDateString() : 'Onbekend'}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500 font-bold truncate mb-2 flex items-center gap-1.5 pointer-events-auto">
                                    {endClientName ? (
                                        <button onClick={() => setSelectedClientModal(selectedVacancy?.eindklant || { id: formData.data?.eindklant_id || selectedVacancy?.eindklant_id, name: endClientName })} className="hover:text-blue-500 hover:underline transition-colors text-left">{endClientName}</button>
                                    ) : null}
                                    {endClientName && opdrachtgeverNameStr && endClientName !== opdrachtgeverNameStr ? <span> - </span> : null}
                                    {opdrachtgeverNameStr && endClientName !== opdrachtgeverNameStr ? (
                                        <button onClick={() => setSelectedClientModal(selectedVacancy?.opdrachtgever || selectedVacancy?.client || { id: formData.data?.opdrachtgever_id || selectedVacancy?.opdrachtgever_id || selectedVacancy?.client_id || placement?.client_id, name: opdrachtgeverNameStr })} className="hover:text-blue-500 hover:underline transition-colors text-left">{opdrachtgeverNameStr}</button>
                                    ) : null}
                                    {!endClientName && !opdrachtgeverNameStr && <span>Geen klant gekoppeld</span>}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/* Modern Tabs */}
                <div className="flex flex-wrap border-b border-slate-100 bg-white px-2 md:px-4 gap-x-1 gap-y-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-3 py-3 text-[9px] md:text-[10px] font-black uppercase tracking-widest border-b-2 transition-all relative top-[1px] whitespace-nowrap ${activeTab === tab.id ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ─── Content ─── */}
                <div className="flex-1 overflow-auto px-6 py-4 relative">
                    {isLoading && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3 shadow-lg rounded-full" />
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Gegevens laden...</p>
                        </div>
                    )}
                    {activeTab === 'overzicht' && (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        {/* Left column - main data */}
                        <div className="xl:col-span-2 space-y-4">
                            {/* Basisgegevens */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                                <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                    <Tag size={13} className="text-slate-400" /> Basisgegevens
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={LABEL}>Nummer</label>
                                        <input type="text" name="number" value={formData.number || ''} onChange={handleChange} className={inputClass} placeholder="PL-xxxxx" />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Kandidaat</label>
                                        {isEditing ? (
                                            <SearchableSelect 
                                                value={formData.candidate_id ? normalizeCandidateOption({ candidate: formData.candidate, id: formData.candidate_id, name: formData.candidate?.name || candidateName, candidate_number: candNum }) : null}
                                                onChange={(val) => setFormData((prev: any) => ({
                                                    ...prev,
                                                    candidate_id: val?.id || null,
                                                    candidate: val?.candidate || val || null,
                                                }))}
                                                onSearch={searchCandidates}
                                                getLabel={formatCandidateLabel}
                                                getValue={(opt: any) => opt}
                                                renderOption={renderCandidateOption}
                                                getOptionKey={(opt: any) => opt.id}
                                                placeholder="Zoek kandidaat (min 2 letters)…"
                                                minChars={2}
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2 group/link pointer-events-auto">
                                                <div className={inputClass}>{candidateName ? `${candidateName} ${candNum ? `(${candNum})` : ''}`.trim() : '—'}</div>
                                                {formData.candidate_id && (
                                                    <button onClick={() => setSelectedCandidateModalId(formData.candidate_id)} className="opacity-0 group-hover/link:opacity-100 p-0.5 text-blue-500 hover:bg-blue-50 rounded transition-all">
                                                        <ExternalLink size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={LABEL}>Vacature</label>
                                        {isEditing ? (
                                            <SearchableSelect 
                                                value={formData.vacancy_id ? { ...(formData.vacancy || {}), id: formData.vacancy_id, title: formData.vacancy?.title || vacancyTitle, vacancy_number: formData.vacancy?.vacancy_number || vacNum } : null}
                                                onChange={(val) => setFormData((prev: any) => ({
                                                    ...prev,
                                                    vacancy_id: val?.id || null,
                                                    vacancy: val || null,
                                                    end_client: prev.end_client || val?.eindklant?.name || val?.client?.name || '',
                                                    data: {
                                                        ...prev.data,
                                                        opdrachtgever: prev.data?.opdrachtgever || val?.opdrachtgever?.name || val?.client?.name || '',
                                                        opdrachtgever_id: prev.data?.opdrachtgever_id || val?.opdrachtgever_id || val?.client_id || null,
                                                        eindklant_id: prev.data?.eindklant_id || val?.eindklant_id || null,
                                                        eindklant: prev.data?.eindklant || val?.eindklant?.name || null,
                                                        werkplek: prev.data?.werkplek || val?.data?.location || val?.data?.werkplek || '',
                                                    },
                                                }))}
                                                onSearch={searchVacancies}
                                                getLabel={formatVacancyLabel}
                                                getValue={(opt: any) => opt}
                                                renderOption={renderVacancyOption}
                                                getOptionKey={(opt: any) => opt.id}
                                                placeholder="Zoek vacature (titel, nummer, klant)…"
                                                minChars={0}
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2 group/link pointer-events-auto">
                                                <div className={inputClass}>{vacancyTitle ? `${vacancyTitle} ${vacNum ? `(${vacNum})` : ''}`.trim() : '—'}</div>
                                                {formData.vacancy_id && (
                                                    <button onClick={() => setSelectedVacancyModalId(formData.vacancy_id)} className="opacity-0 group-hover/link:opacity-100 p-0.5 text-blue-500 hover:bg-blue-50 rounded transition-all">
                                                        <ExternalLink size={12} />

                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={LABEL}>Werkplek / Eindklant</label>
                                        {isEditing ? (
                                            <SearchableSelect 
                                                value={formData.data?.eindklant_id ? { id: formData.data.eindklant_id, name: formData.end_client } : (formData.end_client ? { name: formData.end_client } : null)}
                                                onChange={(val) => setFormData((prev: any) => ({ 
                                                    ...prev, 
                                                    end_client: val?.name || val, 
                                                    data: { ...prev.data, eindklant_id: val?.id, eindklant_logo: val?.logo_url } 
                                                }))}
                                                onSearch={searchClients}
                                                getLabel={(opt: any) => opt.name || opt}
                                                getValue={(opt: any) => opt}
                                                placeholder="Zoek eindklant…"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2 group/link pointer-events-auto">
                                                {formData.data?.eindklant_logo && <img src={formData.data.eindklant_logo} alt="Logo" className="h-4 w-4 object-contain rounded" />}
                                                <div className={inputClass}>{formData.end_client || '—'}</div>
                                                {(formData.end_client || selectedVacancy?.eindklant) && (
                                                    <button onClick={() => setSelectedClientModal(selectedVacancy?.eindklant || { name: formData.end_client })} className="opacity-0 group-hover/link:opacity-100 p-0.5 text-blue-500 hover:bg-blue-50 rounded transition-all">
                                                        <ExternalLink size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={LABEL}>Contactpersoon Klant</label>
                                        {isEditing ? (
                                            <SearchableSelect 
                                                value={
                                                    formData.data?.billing_contact_id
                                                        ? {
                                                              id: formData.data.billing_contact_id,
                                                              first_name: (formData.data.billing_contact || '').split(' ')[0] || formData.data.billing_contact,
                                                              last_name: (formData.data.billing_contact || '').split(' ').slice(1).join(' ') || '',
                                                          }
                                                        : formData.data?.billing_contact
                                                          ? { name: formData.data.billing_contact }
                                                          : null
                                                }
                                                onChange={(val) => setFormData((prev: any) => ({
                                                    ...prev,
                                                    data: {
                                                        ...prev.data,
                                                        billing_contact: val?.first_name
                                                            ? `${val.first_name} ${val.last_name || ''}`.trim()
                                                            : (val?.name || val),
                                                        billing_contact_id: val?.id || null,
                                                    },
                                                }))}
                                                onSearch={searchContacts}
                                                getLabel={(opt: any) => opt.first_name ? `${opt.first_name} ${opt.last_name || ''}`.trim() : (opt.name || opt)}
                                                getValue={(opt: any) => opt}
                                                placeholder="Zoek contactpersoon…"
                                            />
                                        ) : (
                                            <div className={inputClass}>{formData.data?.billing_contact || '—'}</div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={LABEL}>Opdrachtgever</label>
                                        {isEditing ? (
                                            <SearchableSelect 
                                                value={formData.data?.opdrachtgever_id ? { id: formData.data.opdrachtgever_id, name: formData.data?.opdrachtgever } : (formData.data?.opdrachtgever ? { name: formData.data.opdrachtgever } : null)}
                                                onChange={(val) => setFormData((prev: any) => ({ 
                                                    ...prev, 
                                                    data: { ...prev.data, opdrachtgever: val?.name || val, opdrachtgever_id: val?.id, opdrachtgever_logo: val?.logo_url } 
                                                }))}
                                                onSearch={searchClients}
                                                getLabel={(opt: any) => opt.name || opt}
                                                getValue={(opt: any) => opt}
                                                placeholder="Zoek opdrachtgever…"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2 group/link pointer-events-auto">
                                                {formData.data?.opdrachtgever_logo && <img src={formData.data.opdrachtgever_logo} alt="Logo" className="h-4 w-4 object-contain rounded" />}
                                                <div className={inputClass}>{formData.data?.opdrachtgever || '—'}</div>
                                                {(formData.data?.opdrachtgever || selectedVacancy?.opdrachtgever || selectedVacancy?.client) && (
                                                    <button onClick={() => setSelectedClientModal(selectedVacancy?.opdrachtgever || selectedVacancy?.client || { name: formData.data?.opdrachtgever })} className="opacity-0 group-hover/link:opacity-100 p-0.5 text-blue-500 hover:bg-blue-50 rounded transition-all">
                                                        <ExternalLink size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={LABEL}>Opmerkingen</label>
                                        {isEditing ? (
                                            <input type="text" name="opmerkingen" value={formData.data?.opmerkingen || ''} onChange={handleDataChange} className={inputClass} placeholder="Korte opmerking..." />
                                        ) : (
                                            <div className={inputClass}>{formData.data?.opmerkingen || '—'}</div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={LABEL}>Recruiter</label>
                                        {isEditing ? (
                                            <SearchableSelect 
                                                value={formData.recruiter_id ? { id: formData.recruiter_id, name: recruiterName } : (recruiterName ? { name: recruiterName } : null)}
                                                onChange={(val) => setFormData((prev: any) => ({ 
                                                    ...prev, 
                                                    recruiter_id: val?.id,
                                                    recruiter: val
                                                }))}
                                                onSearch={searchRecruiters}
                                                getLabel={(opt: any) => opt.name || opt}
                                                getValue={(opt: any) => opt}
                                                placeholder="Zoek recruiter…"
                                                minChars={0}
                                            />
                                        ) : (
                                            <div className={inputClass}>{recruiterName || '—'}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Contract & Looptijd */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                                <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                    <FileText size={13} className="text-slate-400" /> Contract & Looptijd
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={LABEL}>Startdatum</label>
                                        <input type="date" name="start_date" value={formData.start_date || ''} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Einddatum</label>
                                        <input type="date" name="end_date" value={formData.end_date || ''} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div className="col-span-2 grid grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label className={LABEL}>Type Plaatsing</label>
                                            {isEditing ? (
                                                <select
                                                    name="placement_type"
                                                    value={formData.placement_type || ''}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-bold bg-white text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 cursor-pointer shadow-sm transition-all appearance-none"
                                                    style={{
                                                        backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: 'right .7rem top 50%',
                                                        backgroundSize: '.6rem auto',
                                                    }}
                                                >
                                                    <option value="">— Selecteer type —</option>
                                                    {(clientSettings?.placement_types || ['Deta-vast', 'Detachering', 'Freelance', 'Werving & Selectie']).map((t: string) => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className={inputClass}>{formData.placement_type || '—'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL}>Type Dienstverband</label>
                                            {isEditing ? (
                                                <select
                                                    name="employment_type"
                                                    value={formData.employment_type || ''}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-bold bg-white text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 cursor-pointer shadow-sm transition-all appearance-none"
                                                    style={{
                                                        backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: 'right .7rem top 50%',
                                                        backgroundSize: '.6rem auto',
                                                    }}
                                                >
                                                    <option value="">— Selecteer type —</option>
                                                    {(clientSettings?.employment_types || ['Voltijd', 'Deeltijd', 'Oproep', 'Stage']).map((t: string) => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className={inputClass}>{formData.employment_type || '—'}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL}>Type Contract</label>
                                            {isEditing ? (
                                                <select
                                                    name="contract_type"
                                                    value={formData.contract_type || ''}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-bold bg-white text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 cursor-pointer shadow-sm transition-all appearance-none"
                                                    style={{
                                                        backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundPosition: 'right .7rem top 50%',
                                                        backgroundSize: '.6rem auto',
                                                    }}
                                                >
                                                    <option value="">— Selecteer type —</option>
                                                    {(clientSettings?.contract_types || ['Bepaalde Tijd', 'Onbepaalde Tijd', 'Projectbasis']).map((t: string) => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className={inputClass}>{formData.contract_type || formData.data?.contract_type || '—'}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-span-2 flex items-center gap-6 pt-1">
                                        <label className={`flex items-center gap-2 text-[12px] font-semibold text-slate-600 ${isEditing ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
                                            <input type="checkbox" name="contract_signed" checked={!!formData.contract_signed} onChange={handleChange} disabled={!isEditing} className="rounded text-[#e51b23] focus:ring-[#e51b23]" />
                                            Contract getekend
                                        </label>
                                        <label className={`flex items-center gap-2 text-[12px] font-semibold text-slate-600 ${isEditing ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
                                            <input type="checkbox" name="extensions_allowed" checked={!!formData.data?.extensions_allowed} onChange={handleDataChange} disabled={!isEditing} className="rounded text-[#e51b23] focus:ring-[#e51b23]" />
                                            Verlengingen toegestaan
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right column - linked entity panels */}
                        <div className="space-y-4">
                            {/* Kandidaat Panel */}
                            <div
                                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group"
                                onClick={() => (formData.candidate_id || bullhornCandidate?.id) && window.open(`/?candidateId=${formData.candidate_id || bullhornCandidate?.id}`, '_blank')}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 bg-blue-50 rounded-full flex items-center justify-center">
                                            <UserIcon size={13} className="text-blue-500" />
                                        </div>
                                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Kandidaat</span>
                                    </div>
                                    {(formData.candidate_id || bullhornCandidate) && (
                                        <ExternalLink size={13} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                                    )}
                                </div>
                                {selectedCandidate ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div className="text-[13px] font-bold text-slate-800">{selectedCandidate.name}</div>
                                            {candNum && <span className="text-[9px] font-black bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded uppercase">#{candNum}</span>}
                                        </div>
                                        <div className="text-[11px] text-slate-500">{selectedCandidate.email || '—'}</div>
                                        <div className="text-[11px] text-slate-500">{selectedCandidate.phone || '—'}</div>
                                    </div>
                                ) : bullhornCandidate ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div className="text-[13px] font-bold text-slate-800">{bullhornCandidate.firstName} {bullhornCandidate.lastName}</div>
                                            {candNum && <span className="text-[9px] font-black bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded uppercase">#{candNum}</span>}
                                        </div>
                                        <div className="text-[11px] text-slate-400 italic">Bullhorn kandidaat</div>
                                    </div>
                                ) : (
                                    <div className="text-[12px] text-slate-400 italic">Geen kandidaat geselecteerd</div>
                                )}
                            </div>

                            {/* Vacature Panel */}
                            <div
                                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 cursor-pointer hover:border-emerald-300 hover:shadow-md transition-all group"
                                onClick={() => (formData.vacancy_id || bullhornVacancy?.id) && window.open(`/?vacancyId=${formData.vacancy_id || bullhornVacancy?.id}`, '_blank')}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center">
                                            <Briefcase size={13} className="text-emerald-500" />
                                        </div>
                                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Vacature</span>
                                    </div>
                                    {(formData.vacancy_id || bullhornVacancy) && (
                                        <ExternalLink size={13} className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
                                    )}
                                </div>
                                {selectedVacancy ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div className="text-[13px] font-bold text-slate-800">{selectedVacancy.title}</div>
                                            {vacNum && <span className="text-[9px] font-black bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded uppercase">#{vacNum}</span>}
                                        </div>
                                        <div className="text-[11px] text-slate-500">{selectedVacancy.opdrachtgever?.name || selectedVacancy.client?.name || '—'}</div>
                                        {selectedVacancy.eindklant?.name && (
                                            <div className="text-[11px] text-slate-500">Eindklant: {selectedVacancy.eindklant.name}</div>
                                        )}
                                    </div>
                                ) : bullhornVacancy ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div className="text-[13px] font-bold text-slate-800">{bullhornVacancy.title}</div>
                                            {vacNum && <span className="text-[9px] font-black bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded uppercase">#{vacNum}</span>}
                                        </div>
                                        <div className="text-[11px] text-slate-500">{formData.data?.clientCorporation?.name || bullhornVacancy?.clientCorporation?.name || '—'}</div>
                                        <div className="text-[11px] text-slate-400 italic">Bullhorn vacature</div>
                                    </div>
                                ) : (
                                    <div className="text-[12px] text-slate-400 italic">Geen vacature geselecteerd</div>
                                )}
                            </div>

                            {/* Contract status summary */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                                <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-3">Contractstatus</div>
                                <div className="space-y-2">
                                    <div className={`flex items-center gap-2 text-[12px] font-medium ${formData.contract_signed ? 'text-emerald-600' : 'text-slate-400'}`}>
                                        <CheckCircle size={14} className={formData.contract_signed ? 'text-emerald-500' : 'text-slate-300'} />
                                        Contract {formData.contract_signed ? 'getekend' : 'nog niet getekend'}
                                    </div>
                                    <div className={`flex items-center gap-2 text-[12px] font-medium ${formData.data?.extensions_allowed ? 'text-blue-600' : 'text-slate-400'}`}>
                                        <Clock size={14} className={formData.data?.extensions_allowed ? 'text-blue-500' : 'text-slate-300'} />
                                        Verlenging {formData.data?.extensions_allowed ? 'toegestaan' : 'niet toegestaan'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'financieel' && (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 max-w-3xl">
                        <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                            <DollarSign size={13} className="text-slate-400" /> Financiële Gegevens
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { label: 'Salaris', name: 'salary', type: 'number', base: true },
                                { label: 'Factuurtarief', name: 'billing_rate', type: 'number', base: true },
                                { label: 'Betaaltarief', name: 'pay_rate', type: 'number', base: true },
                                { label: 'Plaatsingskosten', name: 'placement_fee', type: 'number', base: false },
                                { label: 'Betalingseenheid', name: 'payment_unit', type: 'text', base: false, placeholder: 'bijv. Per uur' },
                                { label: '% Toeslag', name: 'surcharge_percentage', type: 'number', base: false },
                                { label: 'Ingangsdatum Betaling', name: 'payment_start_date', type: 'date', base: false },
                                { label: 'Ingangsdatum Factuur', name: 'billing_start_date', type: 'date', base: false },
                                { label: 'Uren per dag', name: 'hours_per_day', type: 'number', base: false },
                                { label: 'Reiskostenvergoeding', name: 'travel_allowance', type: 'text', base: false },
                            ].map(f => (
                                <div key={f.name}>
                                    <label className={LABEL}>{f.label}</label>
                                    <input
                                        type={f.type}
                                        name={f.name}
                                        value={f.base ? (formData[f.name] || '') : (formData.data?.[f.name] || '')}
                                        onChange={f.base ? handleChange : handleDataChange}
                                        placeholder={(f as any).placeholder}
                                        className={inputClass}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'notities' && (
                    <div className="h-full min-h-[400px]">
                        <PlacementNotes placementId={placement.id} currentUser={user} />
                    </div>
                )}

                {activeTab === 'documenten' && (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 max-w-3xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                <FileText size={13} className="text-slate-400" /> Documenten
                            </h3>
                            {canEditPlacementDocs ? (
                                <label
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-colors ${
                                        docUploading || isSaving
                                            ? 'bg-slate-200 text-slate-400 cursor-wait'
                                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer'
                                    }`}
                                >
                                    <Upload size={14} /> {docUploading || isSaving ? 'Bezig…' : 'Uploaden'}
                                    <input
                                        type="file"
                                        className="hidden"
                                        disabled={docUploading || isSaving}
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = '';
                                            if (!file) return;
                                            await handlePlacementDocumentUpload(file);
                                        }}
                                    />
                                </label>
                            ) : (
                                <p className="text-[11px] text-slate-500 font-medium max-w-xs text-right">
                                    Sla de plaatsing eerst op om documenten te kunnen toevoegen.
                                </p>
                            )}
                        </div>

                        {/* Merged Document List: Uploaded + Bullhorn */}
                        <div className="space-y-3">
                            {/* Uploaded Documents */}
                            {formData.data?.uploaded_documents?.map((file: any) => (
                                <div key={file.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                                            <FileText size={14} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[13px] font-bold text-slate-800 truncate">{file.name || 'Document'}</div>
                                            <div className="text-[11px] text-slate-500">
                                                {file.dateAdded ? new Date(file.dateAdded).toLocaleDateString() : 'Onbekend'} - {file.source || 'Lokaal'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {(file.storage_path || file.public_url) && (
                                            <button
                                                type="button"
                                                title="Openen"
                                                onClick={() => {
                                                    if (file.storage_path) void openStorageDocument(file.storage_path);
                                                    else if (file.public_url) window.open(file.public_url, '_blank', 'noopener,noreferrer');
                                                }}
                                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            >
                                                <ExternalLink size={14} />
                                            </button>
                                        )}
                                        {canEditPlacementDocs && (
                                            <button
                                                type="button"
                                                disabled={isSaving}
                                                onClick={() => setDocPendingDelete(file)}
                                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                                                title="Document verwijderen"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Bullhorn Documents */}
                            {formData.data?.fileAttachments?.data?.map((file: any) => (
                                <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center shrink-0">
                                            <FileText size={14} />
                                        </div>
                                        <div>
                                            <div className="text-[13px] font-bold text-slate-800">{file.name || 'Document'}</div>
                                            <div className="text-[11px] text-slate-500">
                                                {file.dateAdded ? new Date(file.dateAdded).toLocaleDateString() : 'Onbekend'} - Bullhorn
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {(!formData.data?.uploaded_documents?.length && (!formData.data?.fileAttachments?.data || formData.data.fileAttachments.data.length === 0)) && (
                                <div className="p-6 text-center text-slate-500 text-sm">
                                    <p className="italic text-slate-400 mb-2">Geen documenten gevonden bij deze plaatsing.</p>
                                    {canEditPlacementDocs ? (
                                        <p className="text-[12px] font-medium text-slate-600">
                                            Gebruik <span className="font-black text-slate-800">Uploaden</span> hierboven om bestanden toe te voegen (ook zonder «Bewerken»).
                                        </p>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
            {selectedClientModal && (
                <ClientDetailModal 
                    client={selectedClientModal}
                    currentUserRole={user.role}
                    currentUserClientId={user.clientId}
                    accessProfiles={[]}
                    onClose={() => setSelectedClientModal(null)}
                    onSaveSuccess={(updatedClient: any) => { 
                        setSelectedClientModal(null);
                        if (updatedClient) {
                            setFormData((prev: any) => {
                                const nd = { ...prev.data };
                                if (updatedClient.id === (prev.data?.eindklant_id || prev.vacancy?.eindklant_id)) {
                                    nd.eindklant_logo = updatedClient.logo_url;
                                    prev.end_client = updatedClient.name;
                                }
                                if (updatedClient.id === (prev.data?.opdrachtgever_id || prev.vacancy?.client_id || prev.vacancy?.opdrachtgever_id || prev.client_id)) {
                                    nd.opdrachtgever_logo = updatedClient.logo_url;
                                    nd.opdrachtgever = updatedClient.name;
                                }
                                return { ...prev, data: nd };
                            });
                        }
                    }}
                />
            )}

            {selectedCandidateModalId && (
                <CandidateDetailModal 
                    candidateId={selectedCandidateModalId}
                    clientId={user.clientId}
                    currentUser={user}
                    onClose={() => setSelectedCandidateModalId(null)}
                    onUpdate={() => {}}
                />
            )}

            {selectedVacancyModalId && (
                <VacancyDetailModal 
                    vacancyId={selectedVacancyModalId}
                    currentUser={user}
                    onClose={() => setSelectedVacancyModalId(null)}
                />
            )}
        </div>
    );
};

export default PlacementDetail;
